import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkles, Send, RotateCcw, ArrowLeft } from 'lucide-react';
import {
  DemoInputField,
  useNaturalTyping,
  AIReasoningAnimation,
  VendorReportCard,
  DEMO_USER_QUERY,
} from '@/components/demo';

type DemoPhase = 'idle' | 'typing' | 'reasoning' | 'results';

export default function DemoChatPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<DemoPhase>('idle');
  const [showUserMessage, setShowUserMessage] = useState(false);

  const handleTypingComplete = useCallback(() => {
    setShowUserMessage(true);
    setPhase('reasoning');
  }, []);

  const { typedText, isTyping } = useNaturalTyping(
    phase === 'typing',
    handleTypingComplete
  );

  const handleInputFocus = useCallback(() => {
    if (phase !== 'idle') return;
    setTimeout(() => {
      setPhase('typing');
    }, 300);
  }, [phase]);

  const handleReasoningComplete = useCallback(() => {
    setPhase('results');
  }, []);

  const handleReset = useCallback(() => {
    setPhase('idle');
    setShowUserMessage(false);
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F5]">
      <div className="relative max-w-4xl mx-auto p-6 min-h-screen flex flex-col">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          onClick={() => navigate('/analysis')}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-[#063F6B] transition-colors mb-4 self-start"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Analysis
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex items-center justify-between mb-10"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#063F6B] rounded-xl flex items-center justify-center shadow-sm">
              <Sparkles className="w-6 h-6 text-[#EAA059]" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-[#063F6B] tracking-tight">
                AI Vendor Discovery
              </h1>
              <p className="text-gray-400 text-sm">
                Vendor recommendations powered by AI
              </p>
            </div>
          </div>

          {phase === 'results' && (
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-sm text-[#063F6B] font-medium shadow-sm hover:shadow hover:border-gray-200 transition-all duration-200"
            >
              <RotateCcw className="w-4 h-4" />
              Reset Demo
            </motion.button>
          )}
        </motion.div>

        {/* Chat Area */}
        <div className="flex-1 space-y-6 overflow-y-auto pb-32">
          {/* Welcome Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex gap-4"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#063F6B] text-[#EAA059] flex-shrink-0 shadow-sm">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-none px-6 py-5 max-w-2xl shadow-sm">
              <p className="text-[#063F6B] text-sm leading-relaxed">
                Welcome! I'm your AI vendor discovery assistant. Tell me about your use case
                and I'll analyze our knowledge base of 847+ vendors to find the best match
                for your needs.
              </p>
              <p className="text-gray-300 text-xs mt-3">
                Click the input field below to begin
              </p>
            </div>
          </motion.div>

          {/* User Message */}
          <AnimatePresence>
            {showUserMessage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                className="flex gap-4 justify-end"
              >
                <div className="bg-[#063F6B] rounded-2xl rounded-tr-none px-6 py-5 max-w-2xl shadow-sm">
                  <p className="text-white text-sm leading-relaxed">
                    {DEMO_USER_QUERY}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-[#063F6B] flex-shrink-0">
                  <div className="w-5 h-5 rounded-full bg-gray-400" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* AI Reasoning Animation */}
          <AnimatePresence>
            {phase === 'reasoning' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                className="flex gap-4"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#063F6B] text-[#EAA059] flex-shrink-0 shadow-sm">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1 max-w-2xl">
                  <AIReasoningAnimation
                    isActive={phase === 'reasoning'}
                    onComplete={handleReasoningComplete}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Results */}
          <AnimatePresence>
            {phase === 'results' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                className="flex gap-4"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#063F6B] text-[#EAA059] flex-shrink-0 shadow-sm">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <VendorReportCard isVisible={phase === 'results'} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Input Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#F9F7F5] via-[#F9F7F5] to-transparent pt-10 pb-6 px-6"
        >
          <div className="max-w-4xl mx-auto pl-14">
            <div className="flex gap-3">
              <div className="flex-1">
                <DemoInputField
                  onFocus={handleInputFocus}
                  typedText={phase === 'typing' ? typedText : ''}
                  isTyping={isTyping}
                  isDisabled={phase !== 'idle'}
                  placeholder="Describe your use case..."
                />
              </div>
              <motion.button
                whileHover={phase === 'idle' ? { scale: 1.03 } : {}}
                whileTap={phase === 'idle' ? { scale: 0.97 } : {}}
                className={`
                  h-12 w-12 rounded-xl flex items-center justify-center transition-all duration-300 shadow-sm
                  ${phase === 'idle'
                    ? 'bg-gradient-to-br from-[#EAA059] to-[#d4893e] hover:shadow-md hover:shadow-[#EAA059]/20'
                    : 'bg-gray-200 opacity-50 cursor-not-allowed'
                  }
                `}
                disabled={phase !== 'idle'}
              >
                <Send className="w-5 h-5 text-white" />
              </motion.button>
            </div>
            <p className="text-center text-xs text-gray-300 mt-3">
              {phase === 'idle' && 'Click the input field to start the demo'}
              {phase === 'typing' && 'Typing...'}
              {phase === 'reasoning' && 'Analyzing vendors...'}
              {phase === 'results' && 'Analysis complete'}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
